import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Container, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ErrorOutline, VisibilityOutlined } from "@mui/icons-material";
import { connect, useDispatch, useSelector } from "react-redux";
import { Dispatch } from "@reduxjs/toolkit";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { RootState } from "../../Store/store";
import { setTogglePropertyClaimedModal } from "../../Redux/propertyReducer";
import { setToggleAuthModal } from "../../Redux/authReducer";
import {
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
  useGetWishlistQuery,
  useGetApnClaimStatusQuery,
} from "../../Services/api.user.slice";
import { checkUserLogin } from "../../types/globalTypes";
import { GoogleMapsProvider } from "../../Contexts/GoogleMapsContext";
import { useOwnerPhotos } from "../../Hooks/useOwnerPhotos";
import { useGoogleSlides } from "../../Hooks/useGoogleSlides";
import { useEntitlements, exitFreePreview } from "../../Hooks/useEntitlements";
import { normalizeCoords, satelliteImageUrl } from "../../Utils/googleMapsImageUrls";
import { setOwnedSince } from "../../Services/ownershipApi";
import OwnerPhotoManager from "./Components/OwnerPhotoManager/OwnerPhotoManager";
import PropertyHeader, { HeaderClaimState } from "./Components/PropertyHeader/PropertyHeader";
import HomeAnniversaryCard from "./Components/PropertyHeader/HomeAnniversaryCard";
import HeaderClaimCard from "./Components/PropertyHeader/HeaderClaimCard";
import PhotoGalleryDialog from "./Components/PropertyHeader/PhotoGalleryDialog";
import DashboardSidebar from "./Components/DashboardSidebar/DashboardSidebar";
import StatsRow from "./Components/StatsRow/StatsRow";
import QuickActions from "./Components/QuickActions/QuickActions";
import MapCard from "./Components/MapCard/MapCard";
import LocalNews from "./Components/News/News";
import HomeStatus from "./Components/HomeStatus/HomeStatus";
import { MaintenanceProvider, useMaintenance } from "./Components/MaintenanceTab/MaintenanceContext";
import { countOverdue } from "./Components/MaintenanceTab/maintenanceSchedule";
import {
  DashboardFocus,
  PropertyDashboardProvider,
  PropertyDashboardValue,
} from "./dashboard/PropertyDashboardContext";
import { DashboardTabId, resolveTabParam, tabLabel } from "./dashboard/dashboardTabs";
import { DASH } from "./dashboard/tokens";
import DashCard from "./dashboard/DashCard";
import { lazyWithPreload } from "../../Utils/lazyWithPreload";
import { usePermitInfo } from "../../Hooks/usePermitSummary";
import type { PermitCoverage, PermitSummary } from "../../Services/permitSummaryApi";
import OnboardingModal from "../../Components/Onboarding/OnboardingModal";
import { useOnboardingTrigger } from "../../Components/Onboarding/useOnboardingTrigger";

// Home Status is the landing tab and ships with the page; the others are
// split out and fetched in the background once the page has settled, so a
// tab switch rarely waits.
const MaintenanceTab = lazyWithPreload(() => import("./Components/MaintenanceTab/MaintenanceTab"));
const HomeHistoryTab = lazyWithPreload(() => import("./Components/HomeHistory/HomeHistoryTab"));
const Documents = lazyWithPreload(() => import("./Components/Documents/Documents"));
const Protection = lazyWithPreload(() => import("./Components/Protection/Protection"));
const PropertyDetailsTab = lazyWithPreload(() => import("./Components/PropertyDetails/PropertyDetailsTab"));
const FindAPro = lazyWithPreload(() => import("./Components/FindAPro/FindAPro"));
const SPLIT_TABS = [MaintenanceTab, HomeHistoryTab, Documents, Protection, PropertyDetailsTab, FindAPro];
const PREFETCH_DELAY_MS = 2500;

/**
 * Phones: scroll to `target()`, then check once more after the page stops
 * moving. Things above can still shift while it scrolls (a photo finishing,
 * the tab's own content arriving), and a first tap landed short. Touching,
 * wheeling or typing cancels the check: the scroll is theirs from then on.
 */
const scrollUnderStrip = (target: () => number | null, smooth: boolean) => {
  const first = target();
  if (first === null) return;
  const behavior: ScrollBehavior = smooth ? "smooth" : "auto";
  if (Math.abs(first - window.scrollY) > 4) window.scrollTo({ top: first, behavior });

  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  const events = ["touchstart", "wheel", "keydown"] as const;
  events.forEach((e) => window.addEventListener(e, cancel, { passive: true }));
  let last = window.scrollY;
  let still = 0;
  let ticks = 0;
  const id = window.setInterval(() => {
    ticks += 1;
    still = window.scrollY === last ? still + 1 : 0;
    last = window.scrollY;
    if (!cancelled && still < 2 && ticks < 30) return; // still moving (up to 3 s)
    window.clearInterval(id);
    events.forEach((e) => window.removeEventListener(e, cancel));
    if (cancelled || still < 2) return;
    const again = target();
    if (again !== null && Math.abs(again - window.scrollY) > 8) window.scrollTo({ top: again, behavior });
  }, 100);
};

/**
 * The property page as the homeowner's "control room" (October 2026 rebuild,
 * SIMPLERHOME_OCTOBER_PLAN.md). Photo header on top, sidebar on the left,
 * the stats row, quick actions and map stay put; only the middle column
 * changes per tab. The active tab lives in the URL (?tab=), so links and
 * the back button work; pre-rebuild links (?tab=home-records, ?trade=) still
 * resolve.
 */
const PropertyDetailPage = (props: any) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTabParam(searchParams.get("tab"), searchParams.get("trade"));
  const [focus, setFocus] = useState<DashboardFocus | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // The active tab's own content, below the stats and Quick Actions.
  const panelRef = useRef<HTMLElement>(null);

  const [managePhotosOpen, setManagePhotosOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const { user, isAuthenticated } = useSelector((state: RootState) => state?.auth);
  const wishlist: any = useSelector((state: RootState) => state.auth?.wishlist) || [];
  const entitlements = useEntitlements();

  const [addToWishlist] = useAddToWishlistMutation({});
  const [removeFromWishlist] = useRemoveFromWishlistMutation({});
  useGetWishlistQuery("", { skip: !isAuthenticated });

  // REAPI propertyId for wishlist / claims / public endpoints; Mongo _id for
  // the owner-scoped APIs (contracts, maintenance, documents).
  const propertyIdForApi = String(props.propertyId || "");
  const propertyRefID = String(props.propertyRefID || "");
  const isInWishlist = wishlist.includes(propertyIdForApi);

  // Poll only while a claim is pending, so an admin approval/rejection surfaces
  // within ~45s even if the user is sitting on the page; stops once resolved.
  const [apnPollMs, setApnPollMs] = useState(0);
  const { data: apnStatusData, refetch: refetchApnStatus } = useGetApnClaimStatusQuery(propertyIdForApi, {
    skip: !isAuthenticated || !propertyIdForApi,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: apnPollMs,
  });
  // PENDING / APPROVED / REJECTED / HAD_OWNED / CANCELLED, or null.
  const apnStatus: string | null = apnStatusData?.data?.status ?? null;
  const ownership = apnStatusData?.data?.ownership as { claimedAt?: string | null; ownedSince?: string | null } | null;

  const listedAsOwner = user?.propertyList?.indexOf(props.propertyRefID) > -1;
  // A previous owner can still appear in the property list; their claim
  // status says HAD_OWNED, so don't hand them the owner dashboard.
  const isOwner = apnStatus === "APPROVED" || (listedAsOwner && apnStatus !== "HAD_OWNED" && apnStatus !== "REJECTED");
  const isClaimed = Boolean(props?.isClaimed) || isOwner;

  useEffect(() => {
    setApnPollMs(apnStatus === "PENDING" ? 45000 : 0);
  }, [apnStatus]);

  // ---- Photos
  const { ownerPhotos, reload: reloadOwnerPhotos } = useOwnerPhotos(propertyIdForApi, user?.token);
  const [hideStreetViewOverride, setHideStreetViewOverride] = useState<boolean | null>(null);
  const hideStreetView = hideStreetViewOverride !== null ? hideStreetViewOverride : Boolean(props?.hideStreetView);
  const rawLat = props?.latitude ?? props?.location?.coordinates?.[1];
  const rawLng = props?.longitude ?? props?.location?.coordinates?.[0];
  const googleSlides = useGoogleSlides(rawLat, rawLng, { hideStreetView });
  const coords = normalizeCoords(rawLat, rawLng);

  // Header background: the owner's featured photo (first in their order) →
  // a wide satellite crop → Street View.
  const headerImages = useMemo(() => {
    const list: string[] = [];
    if (ownerPhotos[0]?.url) list.push(ownerPhotos[0].url);
    if (coords) list.push(satelliteImageUrl(coords.lat, coords.lng, 640, 320, 19));
    const street = googleSlides.find((s) => s.type === "streetview");
    if (street) list.push(street.url);
    return list;
  }, [ownerPhotos, coords, googleSlides]);

  const openManagePhotos = useCallback(() => setManagePhotosOpen(true), []);
  const closeManagePhotos = useCallback(() => setManagePhotosOpen(false), []);

  // ---- Actions
  const requestSignIn = useCallback(() => dispatch(setToggleAuthModal(true)), [dispatch]);

  const requestClaim = useCallback(() => {
    if (!isAuthenticated || !user?.token) {
      dispatch(setToggleAuthModal(true));
    } else if (apnStatus === "PENDING") {
      toast("Your claim is already under review. Please wait for admin approval.", { duration: 4000 });
    } else if (isOwner) {
      toast.success("You have already claimed this property.", { duration: 4000 });
    } else {
      // Someone else owns it: same upload, but it says so and is sent as a
      // dispute (Oct 20 call).
      dispatch(setTogglePropertyClaimedModal({ propertyId: props.propertyId, alreadyClaimed: isClaimed }));
    }
  }, [isAuthenticated, user?.token, apnStatus, isOwner, isClaimed, dispatch, props.propertyId]);

  const handleToggleWishlist = async () => {
    if (!checkUserLogin(user)) {
      toast.error("Please login to add to wishlist");
      dispatch(setToggleAuthModal(true));
      return;
    }
    if (!propertyIdForApi) return;
    try {
      const response = isInWishlist
        ? await removeFromWishlist({ propertyId: propertyIdForApi }).unwrap()
        : await addToWishlist({ propertyId: propertyIdForApi }).unwrap();
      if (!response?.isApiError) {
        toast.success(response?.data?.message || (isInWishlist ? "Removed from wishlist" : "Added to wishlist"));
      }
    } catch (error: any) {
      if (error?.data?.isApiError) toast.error(error?.data?.errors?.message);
    }
  };

  const goTo = useCallback(
    (tab: DashboardTabId, focusTarget?: string) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      // The homepage "Request a service" params only apply on arrival.
      next.delete("trade");
      next.delete("service");
      // A new tab is a history entry, so Back returns to the previous tab.
      // Re-selecting the current one (a quick action within it) isn't.
      setSearchParams(next, { replace: tab === activeTab });
      setFocus(focusTarget ? { target: focusTarget, nonce: Date.now() } : null);
      if (focusTarget) return; // the tab scrolls to what it was asked to show
      const smooth = !window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      // Phones: the stats and Quick Actions between the tab strip and the tab
      // are the same on every tab, so a new tab opened below the screen and
      // nothing seemed to change (Asher, Oct 28). Bring the tab's own content
      // up under the sticky strip; Home Status starts with the stats.
      const strip = document.querySelector<HTMLElement>('[data-testid="dashboard-strip"]');
      if (strip && strip.offsetParent !== null && getComputedStyle(strip).display !== "none") {
        scrollUnderStrip(() => {
          const start = tab === "home-status" ? bodyRef.current : panelRef.current;
          if (!start) return null;
          const gap = tab === "home-status" ? 0 : strip.offsetHeight + 12;
          return Math.max(0, window.scrollY + start.getBoundingClientRect().top - gap);
        }, smooth);
        return;
      }
      // Wider screens: bring the newly shown content into view if we're scrolled past it.
      const top = bodyRef.current?.getBoundingClientRect().top;
      if (typeof top === "number" && top < 0) {
        window.scrollTo({ top: window.scrollY + top - 16, behavior: smooth ? "smooth" : "auto" });
      }
    },
    [searchParams, setSearchParams, activeTab]
  );

  // Warm the other tabs' chunks once the landing tab has had its turn.
  useEffect(() => {
    const id = window.setTimeout(() => SPLIT_TABS.forEach((tab) => tab.preload().catch(() => {})), PREFETCH_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  const theme = useTheme();
  // Wide layout: Quick Actions top the right column. Narrower, that column
  // drops under the tab, so they move above it as a strip of tiles.
  const wide = useMediaQuery(theme.breakpoints.up("lg"), { noSsr: true });

  const saveOwnedSince = useCallback(
    async (value: string | null) => {
      await setOwnedSince(propertyIdForApi, value);
      refetchApnStatus();
    },
    [propertyIdForApi, refetchApnStatus]
  );

  const address = props?.address || {};
  const dashboard: PropertyDashboardValue = {
    propertyId: propertyIdForApi,
    propertyRefID,
    isAuthenticated: Boolean(isAuthenticated),
    isOwner,
    isClaimed,
    apnStatus,
    addressLine1: props.propertyLabel || address.address || "",
    addressLine2: props.propertyAddress || "",
    city: address.city || "",
    state: address.state || "",
    latitude: coords?.lat,
    longitude: coords?.lng,
    goTo,
    requestClaim,
    requestSignIn,
  };

  // Onboarding pop-up (pending / approved tour / rejected). Anchored on the
  // home's claim state, so it fires on a fresh visit, a revisit while already
  // logged in, or a live status flip — see useOnboardingTrigger.
  const onboardingAddress = dashboard.addressLine1 || dashboard.addressLine2 || "";
  const { variant: onboardingVariant, close: closeOnboarding } = useOnboardingTrigger({
    propertyId: propertyIdForApi,
    isAuthenticated: Boolean(isAuthenticated),
    isOwner,
    address: onboardingAddress,
    apn: apnStatusData?.data,
  });

  // The browser tab names the dashboard tab and the home.
  const pageTitle = [tabLabel(activeTab), dashboard.addressLine1, "SimplerHome"].filter(Boolean).join(" · ");
  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previous;
    };
  }, [pageTitle]);

  const claimState: HeaderClaimState = isOwner
    ? "owner"
    : apnStatus === "PENDING"
    ? "pending"
    : isClaimed
    ? "claimed"
    : "unclaimed";

  // Public permits on an unclaimed (or claim-pending) LA home: the reason to
  // claim it, shown in the claim card and on Home History (October 2026).
  const { summary: permitSummary, coverage: permitCoverage } = usePermitInfo(
    propertyIdForApi,
    claimState === "unclaimed" || claimState === "pending"
  );

  const renderAside = (variant: "glass" | "solid") => {
    if (isOwner) {
      return (
        <HomeAnniversaryCard
          ownedSince={ownership?.ownedSince}
          lastSaleDate={props?.propertyOverView?.lastSaleDate}
          claimedAt={ownership?.claimedAt}
          canEdit
          onSave={saveOwnedSince}
          variant={variant}
        />
      );
    }
    if (claimState === "pending") return <HeaderClaimCard pending variant={variant} permitSummary={permitSummary} />;
    if (claimState === "unclaimed")
      return <HeaderClaimCard onClaim={requestClaim} variant={variant} permitSummary={permitSummary} />;
    if (claimState === "claimed") return <HeaderClaimCard claimed onClaim={requestClaim} variant={variant} />;
    return null;
  };
  const mobileAside = renderAside("solid");

  return (
    <GoogleMapsProvider>
      <PropertyDashboardProvider value={dashboard}>
        <MaintenanceProvider propertyId={propertyRefID} enabled={isOwner}>
          <Container maxWidth="xl">
            <FullPaddingWrapper>
              {entitlements.previewingFree && <FreePreviewBanner />}

              <OnboardingModal
                variant={onboardingVariant}
                address={onboardingAddress}
                rejectionReason={apnStatusData?.data?.rejection?.latest ?? null}
                onClose={closeOnboarding}
                onGoToTab={goTo}
                onResubmit={requestClaim}
              />

              <PropertyHeader
                line1={dashboard.addressLine1}
                line2={dashboard.addressLine2}
                imageCandidates={headerImages}
                photoCount={ownerPhotos.length + googleSlides.length}
                onViewPhotos={() => setGalleryOpen(true)}
                claimState={claimState}
                onClaim={requestClaim}
                wishlist={isOwner ? null : { active: isInWishlist, onToggle: handleToggleWishlist }}
                aside={renderAside("glass")}
              />
              {mobileAside && <Box sx={{ display: { xs: "block", md: "none" }, mt: 4 }}>{mobileAside}</Box>}

              <Box
                ref={bodyRef}
                sx={{
                  mt: { xs: 4, md: 8 },
                  pb: 12,
                  display: "grid",
                  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "220px minmax(0, 1fr)", lg: "240px minmax(0, 1fr)" },
                  columnGap: { md: 6 },
                  alignItems: "start",
                }}
              >
                {/* On phones this wrapper dissolves (display: contents) so the
                    tab strip becomes a direct child of this grid. A sticky
                    element only sticks within its parent, and a wrapper just
                    as tall as the strip would never let it stick. */}
                <Box sx={{ display: { xs: "contents", md: "block" }, height: { md: "100%" } }}>
                  <SidebarWithBadges active={activeTab} onSelect={(id) => goTo(id)} isOwner={isOwner} />
                </Box>

                <Box sx={{ minWidth: 0, mt: { xs: 4, md: 0 } }}>
                  <StatsRow overview={props?.propertyOverView} />
                  {!wide && (
                    <Box sx={{ mt: 6 }}>
                      <QuickActions variant="strip" />
                    </Box>
                  )}
                  <Box
                    sx={{
                      mt: 6,
                      display: "grid",
                      gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 320px" },
                      gap: 6,
                      alignItems: "start",
                    }}
                  >
                    <Box
                      component="section"
                      ref={panelRef}
                      aria-label={tabLabel(activeTab)}
                      sx={{ minWidth: 0 }}
                      data-testid={`tab-panel-${activeTab}`}
                    >
                      <TabErrorBoundary key={activeTab} tab={activeTab}>
                        <Suspense fallback={<TabLoading />}>
                          <ActiveTab
                            tab={activeTab}
                            focus={focus}
                            property={props}
                            isOwner={isOwner}
                            locked={isOwner && !entitlements.isPremium}
                            propertyRefID={propertyRefID}
                            onAddPhotos={isOwner ? openManagePhotos : undefined}
                            permitSummary={permitSummary}
                            permitCoverage={permitCoverage}
                            claimPending={claimState === "pending"}
                          />
                        </Suspense>
                      </TabErrorBoundary>
                    </Box>
                    <Stack spacing={4} sx={{ minWidth: 0 }}>
                      {wide && <QuickActions />}
                      <MapCard
                        latitude={rawLat}
                        longitude={rawLng}
                        city={address.city}
                        state={address.state}
                        addressLabel={address.label || dashboard.addressLine1}
                      />
                      <LocalNews city={address.city} state={address.state} />
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </FullPaddingWrapper>
          </Container>

          <PhotoGalleryDialog
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            title={dashboard.addressLine1}
            googleSlides={googleSlides}
            ownerPhotos={ownerPhotos}
            mls={props?.mls}
            propertyId={propertyIdForApi}
            isClaimed={isClaimed}
            canManage={isOwner}
            onManage={openManagePhotos}
          />
          <OwnerPhotoManager
            open={managePhotosOpen}
            onClose={closeManagePhotos}
            propertyId={propertyIdForApi}
            authToken={user?.token}
            photos={ownerPhotos}
            onChange={reloadOwnerPhotos}
            hideStreetView={hideStreetView}
            onHideStreetViewChange={setHideStreetViewOverride}
            freeLimit={isOwner && !entitlements.isPremium ? entitlements.freePhotoLimit : undefined}
          />
        </MaintenanceProvider>
      </PropertyDashboardProvider>
    </GoogleMapsProvider>
  );
};

/** The sidebar, with the overdue-maintenance count on the Maintenance item. */
const SidebarWithBadges: React.FC<{
  active: DashboardTabId;
  onSelect: (id: DashboardTabId) => void;
  isOwner: boolean;
}> = ({ active, onSelect, isOwner }) => {
  const { schedule } = useMaintenance();
  const { isPremium } = useEntitlements();
  const overdue = isOwner && isPremium && schedule ? countOverdue(schedule.items) : 0;
  return <DashboardSidebar active={active} onSelect={onSelect} badges={overdue ? { maintenance: overdue } : {}} />;
};

const ActiveTab: React.FC<{
  tab: DashboardTabId;
  focus: DashboardFocus | null;
  property: any;
  isOwner: boolean;
  locked: boolean;
  propertyRefID: string;
  onAddPhotos?: () => void;
  permitSummary?: PermitSummary | null;
  permitCoverage?: PermitCoverage | null;
  claimPending?: boolean;
}> = ({ tab, focus, property, isOwner, locked, propertyRefID, onAddPhotos, permitSummary, permitCoverage, claimPending }) => {
  switch (tab) {
    case "maintenance":
      return <MaintenanceTab focus={focus} />;
    case "home-history":
      return (
        <HomeHistoryTab
          propertyID={propertyRefID}
          isOwner={isOwner}
          locked={locked}
          focus={focus}
          permitSummary={permitSummary}
          permitCoverage={permitCoverage}
          claimPending={claimPending}
        />
      );
    case "documents":
      return <Documents propertyID={propertyRefID} isOwner={isOwner} locked={locked} focus={focus} />;
    case "protection":
      return <Protection />;
    case "property-details":
      return <PropertyDetailsTab property={property} />;
    case "find-a-pro":
      return <FindAPro focus={focus} />;
    case "home-status":
    default:
      return <HomeStatus lastSaleDate={property?.propertyOverView?.lastSaleDate} onAddPhotos={onAddPhotos} />;
  }
};

/** While a split-out tab's code downloads (rare once the prefetch has run). */
const TabLoading = () => (
  <Box aria-busy="true" aria-label="Loading" data-testid="tab-loading">
    <Skeleton variant="text" width="40%" height={40} />
    <Skeleton variant="text" width="65%" sx={{ mb: 4 }} />
    <Skeleton variant="rounded" height={260} sx={{ borderRadius: DASH.radius }} />
  </Box>
);

/**
 * A tab that fails to load (offline when its code was fetched, or a broken
 * render) says so in place of the middle column. The header, sidebar and
 * right column keep working, and the other tabs are still a click away.
 */
class TabErrorBoundary extends React.Component<{ tab: DashboardTabId; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error(`Property dashboard tab "${this.props.tab}" failed`, error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <DashCard testId="tab-error">
        <Stack spacing={2} alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ErrorOutline sx={{ color: DASH.red }} />
            <Typography sx={{ fontWeight: 700, color: "#1d2b2b" }}>{tabLabel(this.props.tab)} didn't load.</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: DASH.muted }}>
            Check your connection, then reload the page. Your other tabs still work.
          </Typography>
          <Button variant="outlined" size="small" onClick={() => window.location.reload()} sx={{ borderColor: DASH.teal, color: DASH.teal, fontWeight: 700 }}>
            Reload
          </Button>
        </Stack>
      </DashCard>
    );
  }
}

/** Shown while previewing the free plan (?previewTier=free), so testers know why things are locked. */
const FreePreviewBanner = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const exit = () => {
    exitFreePreview();
    if (searchParams.has("previewTier")) {
      const next = new URLSearchParams(searchParams);
      next.delete("previewTier");
      setSearchParams(next, { replace: true });
    }
  };
  return (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={2}
    alignItems={{ xs: "flex-start", sm: "center" }}
    justifyContent="space-between"
    sx={{ mb: 4, px: 4, py: 3, borderRadius: "12px", bgcolor: DASH.amberTint, border: "1px solid rgba(251,190,104,0.7)" }}
    data-testid="free-preview-banner"
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <VisibilityOutlined sx={{ color: DASH.amber }} />
      <Typography sx={{ fontWeight: 700, color: "#5b3a00", fontSize: 14 }}>
        Previewing the free plan. Locked features are what free owners will see once Premium launches.
      </Typography>
    </Stack>
    <Button size="small" onClick={exit} sx={{ color: "#5b3a00", fontWeight: 800, whiteSpace: "nowrap" }}>
      Exit preview
    </Button>
  </Stack>
  );
};

function mapStateToProps(state: RootState) {
  return {
    auth: state.auth,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(PropertyDetailPage);
